/**
 * ExistingDebtCumulativeTable - Aggregated Payment Schedule Display
 * Shows cumulative payments across all existing debt projects.
 * Used in: ProjectWorkflowLayout drawer
 * Part of Frontend Workflow - SPJ-140
 */

import { Box, Text } from '@chakra-ui/react';
import React from 'react';

import { type ColumnDefinition, ReusableTable } from '~/components/tables/ReusableTable';
import { TableWithHeader } from '~/components/tables/TableWithHeader';
import type { DateInputsForm, DateRecord, ExistingDebtProject } from '~/types';
import { formatCurrency } from '~/utils/formatting-utils';

interface ExistingDebtCumulativeTableProps {
  existingDebtProjects: ExistingDebtProject[];
  dateInputs?: DateInputsForm;
  dateRecords?: DateRecord[];
}

interface AggregatedPayment extends Record<string, unknown> {
  paymentDate: string;
  totalPrincipal: number;
  totalInterest: number;
  totalPayment: number;
  fiscalYearEndDate?: string;
  isLastPaymentInFiscalYear?: boolean;
  fiscalYearTotal?: number;
}

/**
 * Aggregates payments across all debt projects by payment date
 * Also calculates fiscal year totals and attaches fiscal year information
 * Uses dateRecords to determine which payment is the last payment of each fiscal year
 */
const aggregatePayments = (
  debtProjects: ExistingDebtProject[],
  dateRecords?: DateRecord[]
): AggregatedPayment[] => {
  const paymentMap = new Map<
    string,
    {
      principal: number;
      interest: number;
      totalPayment: number;
      fiscalYearEndDate?: string;
      isLastPaymentInFiscalYear?: boolean;
    }
  >();

  // Aggregate all payments by date
  debtProjects.forEach((project) => {
    if (!project.existingDebtPaymentRecords || project.existingDebtPaymentRecords.length === 0) {
      return;
    }

    project.existingDebtPaymentRecords.forEach((payment) => {
      const existing = paymentMap.get(payment.paymentDate) || {
        principal: 0,
        interest: 0,
        totalPayment: 0,
      };
      const principalValue = payment.principalPayment || 0;
      const interestValue = payment.totalPaymentInterest || 0;
      // Use totalPayment directly from processed payment record (already calculated correctly via reverse amortization)
      // This matches how the individual table calculates: principal + interestPayment (from reverse amortization)
      const totalPaymentValue = payment.totalPayment || 0;

      // Use fiscal year info from the payment record (should be consistent across projects for same date)
      const fiscalYearEnd = payment.fiscalYearInformation?.fiscalYearEndingDate;

      // Determine if this is the last payment of fiscal year by checking dateRecords
      // For Summer: last payment is May, for Winter: last payment is November
      let isLastPayment = false;
      if (dateRecords && fiscalYearEnd) {
        // Find the dateRecord that contains this payment date
        const matchingDateRecord = dateRecords.find((record) =>
          record.paymentDates.includes(payment.paymentDate)
        );
        // Check if this payment date matches the lastPaymentDateOfFiscalYear
        if (
          matchingDateRecord &&
          matchingDateRecord.lastPaymentDateOfFiscalYear === payment.paymentDate
        ) {
          isLastPayment = true;
        }
      } else {
        // Fallback to payment record info if dateRecords not available
        isLastPayment = payment.fiscalYearInformation?.isFinalPayment || false;
      }

      paymentMap.set(payment.paymentDate, {
        principal: existing.principal + principalValue,
        interest: existing.interest + interestValue, // Use totalPaymentInterest which includes accumulated interest from future payments
        totalPayment: existing.totalPayment + totalPaymentValue, // Use totalPayment from processed records (same as individual table)
        fiscalYearEndDate: fiscalYearEnd || existing.fiscalYearEndDate,
        isLastPaymentInFiscalYear: isLastPayment || existing.isLastPaymentInFiscalYear,
      });
    });
  });

  // Convert to array and sort by date (chronological order - top-down)
  // Use totalPayment directly from the map (which uses totalPayment from processed records)
  // This ensures consistency with how the individual table calculates totals
  const aggregated = Array.from(paymentMap.entries())
    .map(([date, amounts]) => ({
      paymentDate: date,
      totalPrincipal: amounts.principal,
      totalInterest: amounts.interest,
      totalPayment: amounts.totalPayment, // Use totalPayment from processed records (same calculation as individual table)
      fiscalYearEndDate: amounts.fiscalYearEndDate,
      isLastPaymentInFiscalYear: amounts.isLastPaymentInFiscalYear,
    }))
    .sort((a, b) => a.paymentDate.localeCompare(b.paymentDate));

  // Calculate fiscal year totals (sum payments from same fiscal year)
  // Group by fiscal year and sum totals
  const fiscalYearTotals = new Map<string, number>();
  aggregated.forEach((payment) => {
    if (payment.fiscalYearEndDate) {
      const currentTotal = fiscalYearTotals.get(payment.fiscalYearEndDate) || 0;
      fiscalYearTotals.set(payment.fiscalYearEndDate, currentTotal + payment.totalPayment);
    }
  });

  // Attach fiscal year totals to payments (only on last payment of fiscal year)
  const aggregatedWithTotals = aggregated.map((payment) => {
    if (payment.isLastPaymentInFiscalYear && payment.fiscalYearEndDate) {
      const fiscalYearTotal = fiscalYearTotals.get(payment.fiscalYearEndDate);
      return {
        ...payment,
        fiscalYearTotal,
      };
    }
    return payment;
  });

  return aggregatedWithTotals;
};

export const ExistingDebtCumulativeTable: React.FC<ExistingDebtCumulativeTableProps> = ({
  existingDebtProjects,
  dateInputs,
  dateRecords,
}) => {
  const aggregatedPayments = React.useMemo(
    () => aggregatePayments(existingDebtProjects, dateRecords),
    [existingDebtProjects, dateRecords]
  );

  // Get column title based on tax collection schedule
  const fiscalYearColumnTitle = React.useMemo(() => {
    if (!dateInputs?.taxCollectionSchedule) return 'Fiscal Year Total';
    return dateInputs.taxCollectionSchedule === 'summer' ? 'Fiscal Year Total' : 'Levy Year Total';
  }, [dateInputs?.taxCollectionSchedule]);

  // Calculate grand totals
  const grandTotals = React.useMemo(() => {
    const totals = aggregatedPayments.reduce(
      (acc, payment) => ({
        principal: acc.principal + payment.totalPrincipal,
        interest: acc.interest + payment.totalInterest,
        total: acc.total + payment.totalPayment,
      }),
      { principal: 0, interest: 0, total: 0 }
    );
    // Round totals to avoid floating point accumulation errors
    return {
      principal: Math.round(totals.principal),
      interest: Math.round(totals.interest),
      total: Math.round(totals.total),
    };
  }, [aggregatedPayments]);

  if (existingDebtProjects.length === 0) {
    return (
      <Box p={4}>
        <Text fontSize="sm" color="gray.500" textAlign="center">
          No existing debt projects configured
        </Text>
      </Box>
    );
  }

  if (aggregatedPayments.length === 0) {
    return (
      <Box p={4}>
        <Text fontSize="sm" color="gray.500" textAlign="center">
          No payment data available
        </Text>
      </Box>
    );
  }

  // Footer row data - need to match the accessor format
  const footerRow: Partial<AggregatedPayment> & Record<string, unknown> = {
    paymentDate: 'GRAND TOTALS',
    totalPrincipal: grandTotals.principal,
    totalInterest: grandTotals.interest,
    totalPayment: grandTotals.total,
    fiscalYearTotal: undefined, // No fiscal year total in footer
  };

  // Define columns for ReusableTable
  const columns: ColumnDefinition<AggregatedPayment>[] = [
    {
      key: 'paymentDate',
      header: 'Payment Date',
      accessor: 'paymentDate',
      headerTextAlign: 'left',
      cellTextAlign: 'left',
    },
    {
      key: 'totalPrincipal',
      header: 'Total Principal',
      accessor: (item) => {
        // Handle footer row - check if it's the footer row object
        if (
          typeof item === 'object' &&
          item !== null &&
          'paymentDate' in item &&
          item.paymentDate === 'GRAND TOTALS'
        ) {
          return formatCurrency((item as typeof footerRow).totalPrincipal || 0, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        }
        return formatCurrency(item.totalPrincipal, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      },
      headerTextAlign: 'right',
      cellTextAlign: 'right',
    },
    {
      key: 'totalInterest',
      header: 'Total Interest',
      accessor: (item) => {
        // Handle footer row
        if (
          typeof item === 'object' &&
          item !== null &&
          'paymentDate' in item &&
          item.paymentDate === 'GRAND TOTALS'
        ) {
          return formatCurrency((item as typeof footerRow).totalInterest || 0, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        }
        return formatCurrency(item.totalInterest, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      },
      headerTextAlign: 'right',
      cellTextAlign: 'right',
    },
    {
      key: 'totalPayment',
      header: 'Total Payment',
      accessor: (item) => {
        // Handle footer row
        if (
          typeof item === 'object' &&
          item !== null &&
          'paymentDate' in item &&
          item.paymentDate === 'GRAND TOTALS'
        ) {
          return formatCurrency((item as typeof footerRow).totalPayment || 0, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        }
        return formatCurrency(item.totalPayment, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      },
      headerTextAlign: 'right',
      cellTextAlign: 'right',
    },
  ];

  // Add fiscal year total column if dateInputs is available
  if (dateInputs) {
    columns.push({
      key: 'fiscalYearTotal',
      header: fiscalYearColumnTitle,
      accessor: (item) => {
        // Handle footer row
        if (
          typeof item === 'object' &&
          item !== null &&
          'paymentDate' in item &&
          item.paymentDate === 'GRAND TOTALS'
        ) {
          return '—';
        }
        // Only show fiscal year total on last payment of fiscal year
        if (item.fiscalYearTotal !== undefined) {
          return formatCurrency(item.fiscalYearTotal, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        }
        return '';
      },
      headerTextAlign: 'right',
      cellTextAlign: 'right',
    });
  }

  return (
    <Box height="100%" display="flex" flexDirection="column" minHeight={0}>
      {/* Table content area - flex: 1 allows it to grow and scroll */}
      <Box flex={1} minHeight={0} display="flex" flexDirection="column">
        <TableWithHeader
          title="Existing Debt Payment Schedule"
          totalRows={aggregatedPayments.length}
        >
          <ReusableTable
            columns={columns}
            data={aggregatedPayments}
            footerRow={footerRow}
            footerRowStyle={{
              fontWeight: 'bold',
              bg: 'white',
              borderTop: '2px solid',
              borderTopColor: 'gray.300',
            }}
          />
        </TableWithHeader>
      </Box>

      {/* Summary Info - flexShrink: 0 keeps it fixed at bottom */}
      <Box p={3} bg="blue.50" borderTop="1px solid" borderColor="blue.200" flexShrink={0}>
        <Text fontSize="xs" color="gray.700">
          Showing combined schedule across {existingDebtProjects.length} debt project
          {existingDebtProjects.length !== 1 ? 's' : ''}
        </Text>
      </Box>
    </Box>
  );
};
